<template>
  <div class="card mb-2" ref="card">
    <div class="card-header bg-primary text-white d-inline-flex">
      <a v-b-toggle="`collapse-inspection-details-card`" class="collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
      <span class="font-weight-bold">
        {{isInspectionServiceEdit ? 'Edit ' : ''}}Inspection Details
      </span>
      <span
        v-if="canUserEdit() && !isInspectionServiceEdit"
        @click="editInspectionDetails" class="text-white ml-auto c-pointer">
        Edit
      </span>
    </div>
    <b-collapse :id="`collapse-inspection-details-card`" v-model="showCollapse">
      <InspectionDetailsForm v-if="isInspectionServiceEdit" />
      <div v-else class="card-body">
        <template v-if="isInspectionServiceImagery(service)">
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Imagery Comparison Before Date
            </div>
            <div class="col-sm-8">
              {{service.imageryAssessmentBeforeDate | dateString}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Imagery Comparison Before Source
            </div>
            <div class="col-sm-8">
              {{ service.beforeSource || 'None' }}
            </div>
          </div>
          <div class="form-group row" v-if="service.beforeSourceNotes">
            <div class="col-sm-4 label-text">
              Imagery Comparison Before Source Notes
            </div>
            <div class="col-sm-8">
              {{ service.beforeSourceNotes || 'None' }}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Imagery Comparison After Date
            </div>
            <div class="col-sm-8">
              {{service.imageryAssessmentAfterDate | dateString}}
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-4 label-text">
              Imagery Comparison After Source
            </div>
            <div class="col-sm-8">
              {{ service.afterSource || 'None' }}
            </div>
          </div>
          <div class="form-group row" v-if="service.afterSourceNotes">
            <div class="col-sm-4 label-text">
              Imagery Comparison After Source Notes
            </div>
            <div class="col-sm-8">
              {{ service.afterSourceNotes || 'None' }}
            </div>
          </div>
        </template>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Landowner Plans for the Property
          </div>
          <div class="col-sm-8">
            {{ service.landownerPropertyPlans || 'None' }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Questions for VOF
          </div>
          <div class="col-sm-8">
            {{ service.questionsForVOF || 'None' }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Observations
          </div>
          <div class="col-sm-8">
            <template v-if="!service.observations || service.observations.length === 0">
              None
            </template>
            <div v-for='observation in service.observations'
            :key='observation.id'>
              <div class="label-text">{{ `${getObservationName(observation.typeId)}?` }}
                <span class="text-normal">
                  {{ getObservationAcknowledgement(observation.ackTypeId) }}
                </span>
              </div>
              <div class="text-normal">
                {{ observation.notes }}
              </div>
              <br>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end">
          <DownloadFile v-if="!isMultiPropertyService()"
            :entityId="service.id"
            :urlToDownloadFrom="this.urlToDownloadFrom"
            :buttonText="'Inspection Report'"
            :contentType="contentType"
            :defaultFileName="'InspectionReport.docx'"
          />
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { LookupModules } from '@/store/moduleConfig';
import CollapseControl from '@/mixins/CollapseControl';
import StatusHelper from '@/mixins/StatusHelper';
import DownloadFile from '@/components/common/DownloadFile.vue';
import InspectionDetailsForm from '@/components/service/InspectionDetailsForm.vue';

export default {
  name: 'InspectionDetailsCard',
  mixins: [CollapseControl, StatusHelper],
  props: ['storeNamespace'],
  components: {
    InspectionDetailsForm,
    DownloadFile,
  },
  data() {
    return {
      showCollapse: false,
      contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
  },
  created() {
    this.$store.dispatch(`${LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.namespace}/fetchOptions`,
      LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_OBSERVATION_TYPES.namespace}/fetchOptions`,
      LookupModules.SERVICE_OBSERVATION_TYPES.url);
    this.$store.dispatch(`${LookupModules.SERVICE_OBSERVATION_ACK_TYPES.namespace}/fetchOptions`,
      LookupModules.SERVICE_OBSERVATION_ACK_TYPES.url);
  },
  computed: {
    ...mapState('Service', {
      service: state => state.currentService,
      isInspectionServiceEdit: state => state.isInspectionServiceEdit,
    }),
    ...mapGetters(LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.namespace, {
      getSourceName: 'getName',
    }),
    ...mapGetters('Service', ['isInspectionServiceImagery']),
    ...mapGetters(LookupModules.SERVICE_OBSERVATION_TYPES.namespace, {
      getObservationName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_OBSERVATION_ACK_TYPES.namespace, {
      getObservationAcknowledgement: 'getName',
    }),
    urlToDownloadFrom() {
      return `/service/${this.service.id}/ExportInspectionFormReport/${this.$route.params.project_id}`;
    },
  },
  methods: {
    editInspectionDetails() {
      this.$store.dispatch('Service/switchToInspectionEdit');
      this.$store.dispatch('Common/startEditMode', 'Inspection');
      this.showCollapse = true;
    },
    isMultiPropertyService() {
      return this.service.associatedPropertyIds.length > 1;
    },
    canUserEdit() {
      // if user is admin they can always edit
      const roles = this.$store.getters['User/getCurrentUser'].idToken ? this.$store.getters['User/getCurrentUser'].idToken.roles.join(', ') : '';
      if (roles.includes('Admin')) {
        return true;
      }

      // if user is staff AND service is open
      if (roles.includes('Staff') && this.isServiceNewOrOpen(this.service.statusId)) {
        return true;
      }

      // if user is staff AND is staffLead they can always edit
      if (roles.includes('Staff') && this.service.staffLeadName === this.$store.getters['User/getCurrentUser'].name) {
        return true;
      }

      // default return false
      return false;
    },
  },
};
</script>
